/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../router/all_routes";
import {
  SectionCard,
  FieldLabel,
  ReadOnlyField,
  BottomActionBar,
  WarningBanner,
  useConfirm,
} from "./shared/components";
import {
  getEmployeeDetails,
  submitHrInitiatedCase,
  searchEmployees,
  EmployeeSearchResult,
  EmployeeDetails,
  formatDate,
} from "./services/eSeparation.service";

type CaseType =
  | "TERMINATION"
  | "DEATH"
  | "DEPUTATION"
  | "RETIREMENT"
  | "TRANSFER_OUT";

const NO_DUES_NOTE =
  "No resignation or approval flow. After initiating, you fill the No-Dues form on the employee's behalf; submitting it sends it straight to the departments. Once they clear, you send it to Establishment and the separation is completed.";

const CASE_OPTIONS: Array<{
  key: CaseType;
  label: string;
  icon: string;
  accent: string;
  note: string;
}> = [
  { key: "TERMINATION", label: "Termination", icon: "ti-circle-minus", accent: "#C0392B", note: NO_DUES_NOTE },
  { key: "DEATH", label: "Death Case", icon: "ti-heart-off", accent: "#5D6D7E", note: NO_DUES_NOTE },
  { key: "DEPUTATION", label: "Deputation", icon: "ti-arrows-left-right", accent: "#993C1D", note: NO_DUES_NOTE },
  { key: "RETIREMENT", label: "Retirement", icon: "ti-hourglass-high", accent: "#4A1A6E", note: NO_DUES_NOTE },
  { key: "TRANSFER_OUT", label: "Transfer-Out", icon: "ti-arrow-right-circle", accent: "#185FA5", note: NO_DUES_NOTE },
];

const HrInitiateCase: React.FC = () => {
  const navigate = useNavigate();
  const routes = all_routes;
  const { confirm, confirmDialog } = useConfirm();

  /* ── Form state ── */
  const [caseType, setCaseType] = useState<CaseType>("TERMINATION");
  const [empNo, setEmpNo] = useState("");
  const [empLoaded, setEmpLoaded] = useState(false);
  const [emp, setEmp] = useState<EmployeeDetails | null>(null);

  /* ── Search-as-you-type lookup ── */
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<EmployeeSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against a stale search response overwriting a newer one.
  const searchSeq = useRef(0);

  const [dateOfLeaving, setDateOfLeaving] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const activeCase = CASE_OPTIONS.find((c) => c.key === caseType)!;

  // Debounced employee filter — fires as HR types the employee number.
  useEffect(() => {
    const q = query.trim();
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const seq = ++searchSeq.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const list = await searchEmployees(q);
        if (seq === searchSeq.current) {
          setResults(list);
          setShowDropdown(true);
        }
      } finally {
        if (seq === searchSeq.current) setSearching(false);
      }
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  const onSelectEmployee = async (item: EmployeeSearchResult) => {
    setShowDropdown(false);
    setResults([]);
    setQuery(item.emp_no);
    setEmpNo(item.emp_no);
    setEmpLoaded(false);
    setLoadingDetails(true);
    try {
      // Pull the full master record so we show the same details as a resignation.
      const res = await getEmployeeDetails(item.emp_no);
      const details = res.data;
      if (details && (details.emp_name || details.emp_no)) {
        setEmp(details);
      } else {
        setEmp({
          emp_no: item.emp_no,
          emp_name: item.emp_name,
          designation: item.designation,
          posting: item.department,
        } as EmployeeDetails);
      }
      setEmpLoaded(true);
    } catch {
      toast.error("Failed to load employee details.");
      setEmp(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSubmit = async () => {
    if (!empLoaded || !emp) {
      toast.warning("Please fetch the concerned employee first.");
      return;
    }
    if (!dateOfLeaving) {
      toast.warning("Please select the date of leaving.");
      return;
    }
    if (!reason.trim()) {
      toast.warning("Please provide a reason.");
      return;
    }

    const ok = await confirm(
      `Initiate a ${activeCase.label.toLowerCase()} case for ${emp.emp_name} (${empNo.trim()})? You will then fill the No-Dues form on their behalf — there is no resignation approval flow.`,
      {
        title: `Confirm ${activeCase.label}`,
        confirmLabel: "Initiate",
        tone: "danger",
      }
    );
    if (!ok) return;

    setSubmitting(true);
    const res = await submitHrInitiatedCase({
      emp_no: empNo.trim(),
      case_type: caseType,
      date_of_leaving: dateOfLeaving,
      reason: reason.trim(),
    });
    setSubmitting(false);
    if (res.success) {
      toast.success(
        `The ${activeCase.label.toLowerCase()} case has been initiated. Please fill the No-Dues form for the employee.`
      );
      const caseId = res.data?.request_id ?? res.data?.data?.request_id;
      if (caseId) {
        navigate(routes.Esepration.noDuesForm, {
          state: { request_id: caseId, hrFill: true },
        });
      } else {
        navigate(routes.Esepration.hrInitiatedCases);
      }
    } else {
      toast.error(
        res.message || "Failed to initiate the case. Please try again."
      );
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content pb-0">
        <div className="mb-3">
          <h3 className="page-title mb-1">Initiate Separation Case</h3>
        </div>

        {/* Intro */}
        <div
          className="card border-0 shadow-sm mb-4"
          style={{ borderRadius: 12 }}
        >
          <div className="card-body d-flex align-items-start gap-3">
            <span
              className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "#E5F0FA",
                color: "#185FA5",
              }}
            >
              <i className="ti ti-briefcase" style={{ fontSize: 20 }} />
            </span>
            <div>
              <div className="fw-semibold mb-1">HR-Initiated Separation</div>
              <small className="text-muted">
                Raise a termination, death, deputation, retirement or
                transfer-out case against an employee. You only provide the date
                of leaving and a reason, then fill the No-Dues form on their behalf.
                These cases follow the No-Dues process only (no resignation
                approval flow).
              </small>
            </div>
          </div>
        </div>

        {/* ── Case type ── */}
        <SectionCard index={0} title="Case Type">
          <div className="row g-2 mb-3">
            {CASE_OPTIONS.map((opt) => {
              const selected = opt.key === caseType;
              return (
                <div className="col-4" key={opt.key}>
                  <button
                    type="button"
                    className="btn w-100 d-flex flex-column align-items-center gap-2 py-3"
                    style={{
                      borderRadius: 12,
                      border: `1px solid ${selected ? opt.accent : "rgba(0,0,0,0.12)"}`,
                      background: selected ? `${opt.accent}12` : "#fff",
                    }}
                    onClick={() => setCaseType(opt.key)}
                  >
                    <i
                      className={`ti ${opt.icon}`}
                      style={{
                        fontSize: 22,
                        color: selected ? opt.accent : "#6c757d",
                      }}
                    />
                    <span
                      className="small"
                      style={{
                        color: selected ? opt.accent : "#6c757d",
                        fontWeight: selected ? 600 : 400,
                      }}
                    >
                      {opt.label}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>
          <WarningBanner message={activeCase.note} type="info" />
        </SectionCard>

        {/* ── Concerned employee ── */}
        <SectionCard index={1} title="Concerned Employee">
          <FieldLabel label="Search by Employee No." required />
          <div className="position-relative" style={{ zIndex: 10 }}>
            <div className="input-group">
              <span className="input-group-text bg-white">
                <i className="ti ti-search text-muted" />
              </span>
              <input
                className="form-control"
                placeholder="Type employee number…"
                inputMode="numeric"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setEmpLoaded(false);
                  setEmp(null);
                }}
                onFocus={() => results.length > 0 && setShowDropdown(true)}
              />
              {searching ? (
                <span className="input-group-text bg-white">
                  <span
                    className="spinner-border spinner-border-sm text-secondary"
                    role="status"
                  />
                </span>
              ) : query.length > 0 ? (
                <button
                  className="input-group-text bg-white border-start-0"
                  type="button"
                  onClick={() => {
                    setQuery("");
                    setResults([]);
                    setShowDropdown(false);
                    setEmpLoaded(false);
                    setEmp(null);
                  }}
                >
                  <i className="ti ti-circle-x text-muted" />
                </button>
              ) : null}
            </div>

            {/* Dropdown of matches */}
            {showDropdown && results.length > 0 && (
              <div
                className="card border shadow-sm position-absolute w-100 mt-1"
                style={{ borderRadius: 12, overflow: "hidden", zIndex: 20 }}
              >
                {results.map((item) => (
                  <button
                    key={item.emp_no}
                    type="button"
                    className="btn text-start d-flex align-items-center gap-2 px-3 py-2 border-bottom rounded-0"
                    onClick={() => onSelectEmployee(item)}
                  >
                    <span
                      className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 15,
                        background: "rgba(0,0,0,0.04)",
                        color: "#185FA5",
                      }}
                    >
                      <i className="ti ti-user" />
                    </span>
                    <span className="flex-grow-1">
                      <span className="d-block fw-medium">
                        {item.emp_name || "—"}
                      </span>
                      <small className="text-muted">
                        {item.emp_no}
                        {item.designation ? `  ·  ${item.designation}` : ""}
                      </small>
                    </span>
                    <i className="ti ti-chevron-right text-muted" />
                  </button>
                ))}
              </div>
            )}
            {showDropdown &&
              !searching &&
              query.trim().length >= 2 &&
              results.length === 0 && (
                <div
                  className="card border shadow-sm position-absolute w-100 mt-1 text-center text-muted small py-3"
                  style={{ borderRadius: 12, zIndex: 20 }}
                >
                  No matching employees found.
                </div>
              )}
          </div>

          {loadingDetails && (
            <div className="d-flex align-items-center gap-2 mt-3">
              <span
                className="spinner-border spinner-border-sm text-secondary"
                role="status"
              />
              <small className="text-muted">Loading employee details…</small>
            </div>
          )}

          {empLoaded && emp && (
            <div
              className="rounded p-3 mt-3"
              style={{
                background: "rgba(0,0,0,0.02)",
                border: "1px solid rgba(0,0,0,0.07)",
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-3">
                <i
                  className="ti ti-circle-check"
                  style={{ color: "#0F6E56" }}
                />
                <span className="fw-semibold" style={{ color: "#0F6E56" }}>
                  Selected Employee
                </span>
              </div>
              <div className="row g-3">
                <div className="col-md-6">
                  <FieldLabel label="Employee Name" />
                  <ReadOnlyField value={emp.emp_name} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Employee No." />
                  <ReadOnlyField value={emp.emp_no || empNo} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Designation" />
                  <ReadOnlyField value={emp.designation} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Regular / Contract" />
                  <ReadOnlyField value={emp.emp_type} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Department" />
                  <ReadOnlyField value={emp.posting} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Place of Posting" />
                  <ReadOnlyField value={emp.place_of_posting || "—"} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Wing" />
                  <ReadOnlyField value={emp.wing || "—"} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Pay Scale" />
                  <ReadOnlyField value={emp.pay_scale} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Date of Joining" />
                  <ReadOnlyField value={formatDate(emp.doj)} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Mobile No." />
                  <ReadOnlyField value={emp.mobile} />
                </div>
                <div className="col-md-6">
                  <FieldLabel label="Email" />
                  <ReadOnlyField value={emp.email} />
                </div>
              </div>
            </div>
          )}
        </SectionCard>

        {/* ── Case details ── */}
        <SectionCard index={2} title="Case Details">
          <div className="row g-3">
            <div className="col-md-6">
              <FieldLabel label="Date of Leaving" required />
              <input
                type="date"
                className="form-control"
                value={dateOfLeaving}
                onChange={(e) => setDateOfLeaving(e.target.value)}
              />
            </div>
          </div>
          <div className="mt-3">
            <FieldLabel label="Reason for Leaving" required />
            <textarea
              className="form-control"
              rows={5}
              placeholder="Provide the reason for this case..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </SectionCard>
      </div>

      <BottomActionBar
        primaryLabel={submitting ? "Initiating…" : `Initiate ${activeCase.label}`}
        primaryIcon="ti-send"
        secondaryLabel="Cancel"
        onPrimary={handleSubmit}
        onSecondary={() => navigate(routes.Esepration.hub)}
        disabled={
          submitting || !empLoaded || !dateOfLeaving || !reason.trim()
        }
      />
      {confirmDialog}
    </div>
  );
};

export default HrInitiateCase;
